/**
 * Appointment Result Record
 * 
 * Immutable record capturing every appointment outcome.
 * Pricing and build data are snapshotted at submission time.
 */

export type AppointmentResultType = "sold" | "no_sale" | "rescheduled" | "no_demo";

export const RESULT_TYPE_CONFIG: Record<AppointmentResultType, { label: string; color: string; bg: string; icon: string }> = {
  sold:        { label: "Sold",        color: "text-success",     bg: "bg-success/10",     icon: "check-circle" },
  no_sale:     { label: "No Sale",     color: "text-destructive", bg: "bg-destructive/10", icon: "x-circle" },
  rescheduled: { label: "Rescheduled", color: "text-warning",     bg: "bg-warning/10",     icon: "rotate-ccw" },
  no_demo:     { label: "No Demo",     color: "text-muted-foreground", bg: "bg-muted/50",  icon: "alert-triangle" },
};

export const NO_SALE_REASON_LABELS: Record<string, string> = {
  price_too_high: "Price too high",
  not_ready: "Not ready",
  shopping_competitors: "Shopping competitors",
  financing_declined: "Financing declined",
  scope_mismatch: "Scope mismatch",
  other: "Other",
};

export const NO_DEMO_REASON_LABELS: Record<string, string> = {
  customer_not_home: "Customer not home",
  porched: "Porched",
  could_not_access: "Could not access",
  rescheduled: "Rescheduled",
  other: "Other",
};

export interface PricingSnapshot {
  standardPrice: number;
  promoPrice: number;
  bestValuePrice: number;
  paymentMethod: string;
  productSummary: string;
}

export interface AppointmentResult {
  id: string;
  appointmentId: string;
  leadId: string;
  opportunityId: string;
  customerName: string;
  assignedRep: string;
  source: string;
  appointmentDatetime: string;
  resultType: AppointmentResultType;
  noSaleReason?: string;
  noDemoReason?: string;
  rescheduleDatetime?: string;
  buildSummary?: string;
  pricingSnapshot?: PricingSnapshot;
  /** Canonical persisted timestamp used for commission date attribution */
  commissionEarnedAt?: string;
  notes: string;
  submittedAt: string;
  submittedBy: string;
  nextAction: string;
  nextActionStatus: "pending" | "completed" | "in_progress";
}

export function getNextAction(resultType: AppointmentResultType, reason?: string): string {
  switch (resultType) {
    case "sold": return "Job creation and financial process";
    case "no_sale": return "Follow-up task assigned";
    case "rescheduled": return "New appointment scheduled";
    case "no_demo":
      if (reason === "rescheduled") return "Reschedule appointment created";
      return "Recovery follow-up assigned";
    default: return "Pending review";
  }
}
